import { BriefDeliverableTaskUploadTypeEnum } from "@entities/brief/lib/constants";
import { IDraftingMediaAssets } from "@shared/lib/types/drafting";
import { ChevronDown, ChevronUp, FileText, ListChecks, Maximize2, Minimize2 } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { DraftAssetPreview } from "./DraftAssetPreview";
import { DraftTranscript } from "./DraftTranscript";
import styles from "./reviewPanel.module.scss";
import tabStyles from "./styles.module.scss";

type ReviewTab = "analysis" | "transcript";

function canSeekAsset(asset?: IDraftingMediaAssets) {
  return (
    asset?.type === BriefDeliverableTaskUploadTypeEnum.Video ||
    asset?.type === BriefDeliverableTaskUploadTypeEnum.YoutubeLink
  );
}

function previewNoun(asset?: IDraftingMediaAssets) {
  if (asset?.type === BriefDeliverableTaskUploadTypeEnum.Image) return "image";
  if (canSeekAsset(asset)) return "video";
  return "preview";
}

export function DraftReviewPanel({
  assets,
  selectedId,
  onSelect,
  draftingId,
  draftingVersionId,
  analysis,
}: {
  assets: IDraftingMediaAssets[];
  selectedId: string;
  onSelect: (assetId: string) => void;
  draftingId: string;
  draftingVersionId: string;
  analysis: ReactNode;
}) {
  const selected = assets.find((asset) => asset._id === selectedId) ?? assets[0];
  const canSeek = canSeekAsset(selected);
  const frameRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<ReviewTab>("analysis");
  const [videoOpen, setVideoOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenPending, setFullscreenPending] = useState(false);
  const transcriptOpen = tab === "transcript" && canSeek;
  const previewOpen = transcriptOpen || videoOpen;
  const previewLabel = previewNoun(selected);
  const lines = selected?.transcription ?? [];

  useEffect(() => {
    setCurrentTime(0);
    setTab("analysis");
    setVideoOpen(false);
  }, [selected?._id]);

  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(document.fullscreenElement === frameRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  useEffect(() => {
    if (!fullscreenPending || !previewOpen) return;
    setFullscreenPending(false);
    void frameRef.current?.requestFullscreen()?.catch(() => undefined);
  }, [fullscreenPending, previewOpen]);

  const showAnalysis = () => {
    setTab("analysis");
    setVideoOpen(false);
  };

  const showTranscript = () => {
    setTab("transcript");
    setVideoOpen(true);
  };

  const toggleFullscreen = () => {
    const frame = frameRef.current;
    if (frame && document.fullscreenElement === frame) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    if (!previewOpen) {
      setVideoOpen(true);
      setFullscreenPending(true);
      return;
    }
    void frame?.requestFullscreen()?.catch(() => undefined);
  };

  return (
    <div className={styles.panel}>
      {assets.length > 1 ? (
        <div className={tabStyles.assetTabs} role="tablist" aria-label="Draft assets">
          {assets.map((asset) => (
            <button
              key={asset._id || asset.url}
              type="button"
              role="tab"
              aria-selected={asset._id === selected?._id}
              className={
                asset._id === selected?._id ? tabStyles.assetTabActive : tabStyles.assetTab
              }
              onClick={() => onSelect(asset._id)}
            >
              {asset.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.videoActions}>
        {transcriptOpen ? (
          <span className={styles.videoHint}>Click a line to jump in the video</span>
        ) : (
          <button
            type="button"
            className={styles.videoToggle}
            onClick={() => setVideoOpen((open) => !open)}
          >
            {previewOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {previewOpen ? `Hide ${previewLabel}` : `Show ${previewLabel}`}
          </button>
        )}
        {previewOpen ? (
          <button type="button" className={styles.videoToggle} onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {isFullscreen ? "Exit full screen" : "Full screen"}
          </button>
        ) : null}
      </div>
      <DraftAssetPreview
        assets={assets}
        selectedId={selectedId}
        draftingId={draftingId}
        draftingVersionId={draftingVersionId}
        collapsed={!previewOpen}
        currentTime={currentTime}
        onTimeChange={setCurrentTime}
        frameRef={frameRef}
      />
      <div className={styles.switcher} role="tablist" aria-label="Draft review">
        <button
          type="button"
          role="tab"
          aria-selected={!transcriptOpen}
          className={!transcriptOpen ? styles.switchActive : styles.switchBtn}
          onClick={showAnalysis}
        >
          <ListChecks size={14} />
          Analysis
        </button>
        {canSeek ? (
          <button
            type="button"
            role="tab"
            aria-selected={transcriptOpen}
            className={transcriptOpen ? styles.switchActive : styles.switchBtn}
            onClick={showTranscript}
          >
            <FileText size={14} />
            Transcript
          </button>
        ) : null}
      </div>
      {transcriptOpen ? (
        <DraftTranscript
          lines={lines}
          status={selected?.transcriptionStatus}
          currentTime={currentTime}
          onSeek={setCurrentTime}
        />
      ) : (
        analysis
      )}
    </div>
  );
}
